// app/src/lib/procurement/priceMovers.js — top product price increases/decreases.
//
// Groups line items by (normalized product name × vendor), then splits a group
// into per-unit series ONLY when the vendor sold the item under two or more
// different units — so "Broccoli Crowns" priced per-case never compares against
// the same crowns priced per-pound (that mismatch produced fantasy +2,082%
// swings), while a lone blank unit still rides with the single real unit instead
// of fragmenting a legitimate series. Two suppliers' prices never mix (owner
// rule — cross-supplier deltas are unit/pack noise). Within a series the trailing
// window's first vs last price is compared; any move over ± SUSPICIOUS_PCT is
// held in `review` rather than shown as a headline.

import { db } from '../db.js';
import { requireUserId } from './util.js';
import { normUom, isSuspiciousMove, unitsConflict } from './priceUnit.js';

const PACK_UNIT = /^(cs|case|cases|lb|lbs|oz|ct|cnt|count|ea|each|pc|pcs|pk|pkg|pack|kg|g|gr|gal|doz|dz|bx|box|qt|pt|ml|ltr|l|x|hash)$/;

// Separator that can't appear in a name/vendor/uom, so composite keys never collide.
const KEY_SEP = String.fromCharCode(31); // ASCII unit separator

/** Normalize a description into a product key: drop leading measurement/pack tokens. */
function normItemName(x) {
  const s = (x || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  const toks = s.split(' ');
  let i = 0;
  while (i < toks.length && (/\d/.test(toks[i]) || PACK_UNIT.test(toks[i]))) i++;
  const out = toks.slice(i).join(' ').trim();
  return out || s;
}

function parseDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const pctMove = (oldC, newC) => ((newC - oldC) / oldC) * 100;
const byDateAsc = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

/**
 * Pure classifier: group rows by product-at-vendor, split into per-unit series
 * only when units genuinely conflict, then compare each series' first vs last
 * in-window price. Splits into increases, decreases, and `review` (moves too
 * large — or too unit-ambiguous — to trust).
 *
 * Rows may carry `inWindow: false` for out-of-window history: those rows VOTE
 * in the unit-conflict decision (so the split doesn't flap as the window
 * slides, and matches the report's full-history scope) but are excluded from
 * the price comparison itself.
 *
 * When a split does happen, the blank-unit series is unit-AMBIGUOUS — each
 * blank point could be either real unit — so its movers always go to `review`,
 * never the headline, regardless of size.
 * @param {Array<{ name:string, vendorId:string, uom:string, date:string, cents:number, label:string, inWindow?:boolean }>} rows
 * @returns {{ increases: Mover[], decreases: Mover[], review: Mover[], reviewCount: number }}
 *   Mover = { label, oldCents, newCents, pct }; reviewCount is the pre-slice total.
 */
export function classifyMoves(rows, n = 5) {
  // 1) One group per product-at-vendor (a per-lb and a per-case observation of
  //    the same item at the same supplier still land together here).
  const groups = {};
  for (const r of rows) {
    if (!r.name || !(r.cents > 0)) continue;
    const g = r.name + KEY_SEP + (r.vendorId || '');
    (groups[g] = groups[g] || []).push(r);
  }

  const clean = [], review = [];
  for (const g of Object.keys(groups)) {
    const pts = groups[g];
    // 2) Split into per-unit series ONLY on a real unit conflict; otherwise a
    //    blank unit rides with the group's single real unit (no fragmentation).
    //    ALL rows vote, including out-of-window history.
    const split = unitsConflict(pts.map((p) => p.uom));
    const series = {};
    for (const p of pts) {
      const sk = split ? normUom(p.uom) : '*';
      (series[sk] = series[sk] || []).push(p);
    }
    // 3) Per series, compare first vs last of the WINDOW (voters excluded).
    for (const sk of Object.keys(series)) {
      const s = series[sk].filter((p) => p.inWindow !== false).sort(byDateAsc);
      if (s.length < 2) continue;
      const first = s[0], last = s[s.length - 1];
      if (!(first.cents > 0) || first.cents === last.cents) continue;
      const mover = { label: last.label, oldCents: first.cents, newCents: last.cents, pct: pctMove(first.cents, last.cents) };
      const ambiguous = split && sk === ''; // blank series under a conflict: unit unknowable
      ((ambiguous || isSuspiciousMove(first.cents, last.cents)) ? review : clean).push(mover);
    }
  }

  return {
    increases: clean.filter((m) => m.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, n),
    decreases: clean.filter((m) => m.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, n),
    review: review.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct)).slice(0, n),
    reviewCount: review.length, // pre-slice total for the "N need a unit check" label
  };
}

/**
 * Top product price movers since a given date (first vs last observed unit price
 * within the window). `fromIso` is an inclusive 'YYYY-MM-DD' lower bound.
 * @returns {Promise<{ increases: Mover[], decreases: Mover[], review: Mover[], reviewCount: number }>}
 */
export async function topMoversSince(fromIso, n = 5) {
  const userId = await requireUserId();
  const invoices = await db.invoices.where('userId').equals(userId)
    .filter((i) => !i.isDuplicate).toArray();
  const dateOf = {}, vendorOf = {};
  invoices.forEach((i) => { dateOf[i.invoiceId] = i.issuedDate || ''; vendorOf[i.invoiceId] = i.vendorId || ''; });

  const cutoff = parseDate(fromIso) || (() => { const c = new Date(); c.setHours(0, 0, 0, 0); c.setDate(c.getDate() - 30); return c; })();

  const lines = await db.lineItems.where('userId').equals(userId).toArray();
  const rows = [];
  for (const l of lines) {
    if (!(l.unitPriceCents > 0) || l.priceExcluded) continue; // priceExcluded: operator hid a bad OCR point
    const name = normItemName(l.description);
    if (!name) continue;
    const date = dateOf[l.invoiceId] || '';
    const dt = parseDate(date);
    if (!dt) continue;
    // Out-of-window history still rides along as a VOTER in the unit-conflict
    // decision (stable split, same scope as the report); only in-window rows
    // take part in the price comparison.
    rows.push({ name, vendorId: vendorOf[l.invoiceId], uom: l.uom, date, cents: l.unitPriceCents, label: l.description, inWindow: dt >= cutoff });
  }

  return classifyMoves(rows, n);
}

/** Top product price movers over the trailing 30 days (delegates to topMoversSince). */
export async function topMovers30d(n = 5) {
  const c = new Date(); c.setHours(0, 0, 0, 0); c.setDate(c.getDate() - 30);
  return topMoversSince(c.toISOString().slice(0, 10), n);
}

/**
 * Order the Price-changes screen's per-invoice list (owner rule 2026-08-11):
 * grouped by supplier, and within each supplier running straight down from the
 * biggest increase to the biggest decrease.
 *
 * The list used to be a date-ordered feed, which interleaved suppliers — so
 * answering "what did this vendor just do to me" meant scanning every row for
 * their name. Grouping puts the vendor in a heading instead, and the monotonic
 * ±% within the group means the rows that cost money are always at the top of
 * their block and the savings are always at the bottom.
 *
 * Sorts a COPY; ties break on label so the order is stable across reloads.
 * @param {Array<{ vendor?:string, pct:number, label?:string }>} rows
 * @returns {Array} a new array
 */
export function sortByVendorThenMove(rows) {
  return [...(rows || [])].sort((a, b) =>
    (a.vendor || '').localeCompare(b.vendor || '')
    || b.pct - a.pct
    || (a.label || '').localeCompare(b.label || ''));
}
